import { logger } from '@rhg/libs/logger';
import { JSONObject } from '@rhg/libs/logger/types';
import {
    differenceInYears,
    differenceInWeeks,
    differenceInCalendarDays,
} from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { capitalizeString } from '../string';

export type TimeOfDay = 'morning' | 'afternoon' | 'evening';

export const TIME_OF_DAY_CUTOFF_HOURS: Record<TimeOfDay, number> = {
    morning: 4,
    afternoon: 12,
    evening: 17,
};

export function getTimeOfDay(date = new Date()): TimeOfDay {
    const hours = date.getHours();

    if (
        hours >= TIME_OF_DAY_CUTOFF_HOURS.morning &&
        hours < TIME_OF_DAY_CUTOFF_HOURS.afternoon
    ) {
        return 'morning';
    }
    if (
        hours >= TIME_OF_DAY_CUTOFF_HOURS.afternoon &&
        hours < TIME_OF_DAY_CUTOFF_HOURS.evening
    ) {
        return 'afternoon';
    }
    return 'evening';
}

function getBrowserLocalePreference(): string {
    return navigator?.language;
}

/**
 * Formats this in the form yyyy-MM-dd (ex. 2021-01-31)
 */
export function getYMD(date = new Date()) {
    return format(date, 'yyyy-MM-dd');
}

/**
 * Will show the date in the RFC3339 format from the UTC time
 */
export function getUTCDateTimeString(date = new Date()): string {
    return date.toISOString();
}

// Cypress sets this to an unknown timezone which causes our useActivities() hook API to fail
const invalidTimezones = ['etc/unknown', ''];
const utcFallback = () => {
    logger.warn('Falling back to UTC timezone');
    return 'UTC';
};

export function getTimeZone() {
    try {
        // eslint-disable-next-line new-cap
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Default to UTC if timezone is invalid
        return typeof timezone !== 'string' ||
            invalidTimezones.includes(timezone.toLowerCase())
            ? utcFallback()
            : timezone;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
        return utcFallback();
    }
}

/**
 * Will show the date in the format "yyyy-MM-dd'T'HH:mm:ssxxx" in the local timezone
 */
export function getLocalDateTimeString(date = new Date()): string {
    try {
        const timeZone = getTimeZone();

        if (timeZone) {
            const zonedDate = utcToZonedTime(date, timeZone);
            return format(zonedDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX", {
                timeZone,
            });
        }
    } catch (error) {
        logger.warn(
            'Error getting local date time string - falling back to ISO string',
            { error: error as JSONObject }
        );
    }

    return date.toISOString();
}

/**
 * Will show the date in the format "yyyy-MM-dd'T'HH:mm:ssxxx" in the UTC timezone
 */
export function getUtcDateTimeString(date = new Date()): string {
    return date.toISOString();
}
/**
 * Adds the time to the date in order to avoid the UTC offset issue (i.e. without it '1/1/2023' becomes '12/31/2022')
 */
export function getUtcDateForMDY(date: string): Date {
    return new Date(`${date}T00:00:00`);
}

/**
 * Offset timezone created by Date fn when using a string date from the db that does not have timezone
 * Ex: DB returns "2035-02-01", but new Date("2035-02-01") adds timezone which makes it "2035-01-31"
 */
export function getOffsetTimezoneDateString(date = new Date()) {
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return displayLocaleDateString(date);
}

export function displayLocaleCustomString(
    options: Intl.DateTimeFormatOptions,
    date = new Date(),
    localeOverride?: string
): string {
    const locale = getBrowserLocalePreference() ?? 'en-US';
    const dateIntl = new Intl.DateTimeFormat(localeOverride ?? locale, options);
    return dateIntl.format(date);
}

/**
 * This is how dates should be displayed to the user
 */
export function displayLocaleDateString(date = new Date()): string {
    return displayLocaleCustomString(
        { year: 'numeric', month: 'short', day: 'numeric' },
        date
    );
}

/**
 * This is how date times should be displayed to the user
 */
export function displayLocaleDateTimeString(date = new Date()): string {
    return displayLocaleCustomString(
        {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        },
        date
    );
}

/**
 * Displays a date time as "10 Dec 2025, 4:20pm".
 *
 * Unlike {@link displayLocaleDateTimeString}, the day always leads and the day
 * period is lowercased and joined to the time, so the layout stays fixed while
 * the month name and numerals still follow the user's locale.
 */
export function displayLocaleDayFirstDateTimeString(date = new Date()): string {
    const locale = getBrowserLocalePreference() ?? 'en-US';
    const parts = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).formatToParts(date);

    const partValue = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value ?? '';

    const time = `${partValue('hour')}:${partValue('minute')}${partValue(
        'dayPeriod'
    ).toLowerCase()}`;

    return `${partValue('day')} ${partValue('month')} ${partValue(
        'year'
    )}, ${time}`;
}

/**
 * Today = 0, Yesterday = -1, Tomorrow = 1, etc.
 * @param dayIndex
 */
export function getLocalizedRelativeDay(dayIndex = 0) {
    const locale = getBrowserLocalePreference() ?? 'en-US';
    const relativeTimeFormat = new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto',
    });

    if (dayIndex > 1 || dayIndex < -1) {
        const date = new Date();
        date.setDate(date.getDate() + dayIndex);

        const dateIntl = new Intl.DateTimeFormat(locale, { weekday: 'short' });
        const formattedDay = dateIntl.format(date);

        return capitalizeString(formattedDay);
    }

    const formattedDay = relativeTimeFormat.format(dayIndex, 'day');

    return capitalizeString(formattedDay);
}

export function yearsAgo(date: Date | number) {
    return differenceInYears(getCurrentTime(), date);
}

export function weeksAgo(date: Date | number) {
    return differenceInWeeks(getCurrentTime(), date);
}

export function daysAgo(date: Date | number) {
    return differenceInCalendarDays(getCurrentTime(), date);
}

export function getCurrentTime() {
    return Date.now();
}

// The age (in years) at which a user is considered an adult.
export const ADULT_AGE_YEARS = 18;

/**
 * Derives whether a user is an adult (18+) from their date of birth.
 *
 * Returns `undefined` when the dob is unknown so callers can omit the value
 * rather than report a misleading `false`.
 *
 * @param dob - The user's date of birth (yyyy-MM-dd).
 */
export function isAdult(dob?: string): boolean | undefined {
    if (!dob) {
        return undefined;
    }
    return yearsAgo(getUtcDateForMDY(dob)) >= ADULT_AGE_YEARS;
}
